import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import ConfirmModal from './ConfirmModal';
import { authHeaders } from '../utils/apiAuth';
import { errorText } from '../utils/apiErrors';
import { CAP } from '../utils/permissions';
// No ReadOnlyNotice here: this component is also embedded as a tab inside
// AdminCustomerDetails, which already shows one, and a second banner inside a
// tab panel reads as noise.
import { usePermissions } from '../utils/PermissionContext';

// ----- Shimmer placeholder: lets the expand panel reserve its final height
// while detail loads, so content never jumps when loading finishes. -----
const Skel = ({ className = '' }) => (
  <span className={`inline-block bg-gray-200 rounded animate-pulse ${className}`} aria-hidden="true" />
);

// Formats human-friendly action messages matching Maventa web UI
const formatActionMessage = (a, t) => {
  if (a.message) return a.message;
  const key = String(a.key || '').toLowerCase();
  const type = String(a.type || '').toUpperCase();
  const channel = String(a.channel || '').toUpperCase();
  const cd = a.channel_details || {};
  const addr = cd.address;
  const eia = cd.eia || cd.address;
  const op = cd.operator || cd.network;

  if (key === 'action_created' || type === 'CREATED') {
    return t?.action_created_label || 'Created';
  }
  if (key === 'action_sent_email' || channel === 'EMAIL') {
    if (addr) return `${t?.sent_by_email_to || 'Sent by email to'} ${addr}`;
    return t?.sent_by_email || 'Sent by email';
  }
  if (key === 'action_sent_relay' || key === 'action_sent_einvoice' || channel === 'EINVOICE') {
    if (eia && op) return `e-Invoice sent to ${eia} - ${op}`;
    if (eia) return `e-Invoice sent to ${eia}`;
    if (op) return `e-Invoice sent via ${op}`;
    return 'e-Invoice sent';
  }
  if (key === 'action_sent_print' || channel === 'PRINT') {
    if (addr) return `Sent by print to ${addr}`;
    return 'Sent by print';
  }
  if (type === 'DELIVERED') {
    if (eia && op) return `Delivered to ${eia} - ${op}`;
    if (eia) return `Delivered to ${eia}`;
    return 'Delivered';
  }
  return null;
};

// ----- Action timeline (styled identically to SortableLinesTable in AdminAfasTransactions.js) -----
const MaventaActionsTimeline = ({ actions, language, t, loading = false }) => {
  const [sortConfig, setSortConfig] = useState({ key: 'happened_at', direction: 'asc' });

  const sortedActions = useMemo(() => {
    let arr = [...(actions || [])];
    if (sortConfig.key !== null) {
      arr.sort((a, b) => {
        const valA = String(a[sortConfig.key] || '').toLowerCase();
        const valB = String(b[sortConfig.key] || '').toLowerCase();
        if (valA < valB) return sortConfig.direction === 'asc' ? -1 : 1;
        if (valA > valB) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return arr;
  }, [actions, sortConfig]);

  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') direction = 'desc';
    setSortConfig({ key, direction });
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return (
      <svg className="w-3 h-3 ml-1 opacity-20 group-hover:opacity-50 transition-opacity" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" /></svg>
    );
    return sortConfig.direction === 'asc' ? (
      <svg className="w-3 h-3 ml-1 text-primary animate-bounce-subtle" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 15l7-7 7 7" /></svg>
    ) : (
      <svg className="w-3 h-3 ml-1 text-primary animate-bounce-subtle" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M19 9l-7 7-7-7" /></svg>
    );
  };

  const channelIcon = (channel) => {
    if (channel === 'EMAIL') return (
      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1.5 text-amber-500 inline" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
    );
    if (channel === 'EINVOICE') return (
      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1.5 text-green-600 inline" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>
    );
    if (channel === 'PRINT') return (
      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1.5 text-gray-500 inline" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" /></svg>
    );
    return null;
  };

  return (
    <div className="mb-6 last:mb-0">
      <h4 className="font-semibold text-gray-700 mb-2 uppercase text-[10px] tracking-widest flex items-center">
        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        {t?.peppol_actions_timeline || 'Delivery Timeline'}
      </h4>
      <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white shadow-sm overflow-hidden">
        <table className="w-full text-sm text-left">
          <thead className="bg-purple-100 uppercase text-primary text-[10px] font-bold tracking-wider">
            <tr>
              <th className="px-4 py-3 border-b border-purple-200 cursor-pointer hover:bg-purple-200/50 transition-colors group" onClick={() => requestSort('channel')}>
                <div className="flex items-center">{t?.channel || 'Channel'} {getSortIcon('channel')}</div>
              </th>
              <th className="px-4 py-3 border-b border-purple-200 cursor-pointer hover:bg-purple-200/50 transition-colors group" onClick={() => requestSort('type')}>
                <div className="flex items-center">{t?.event || 'Event'} {getSortIcon('type')}</div>
              </th>
              <th className="px-4 py-3 border-b border-purple-200 cursor-pointer hover:bg-purple-200/50 transition-colors group" onClick={() => requestSort('message')}>
                <div className="flex items-center">{t?.message || 'Message'} {getSortIcon('message')}</div>
              </th>
              <th className="px-4 py-3 border-b border-purple-200 cursor-pointer hover:bg-purple-200/50 transition-colors group text-right" onClick={() => requestSort('happened_at')}>
                <div className="flex items-center justify-end">{t?.happened_at || 'When'} {getSortIcon('happened_at')}</div>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {loading && Array.from({ length: 3 }).map((_, idx) => (
              <tr key={`skel-${idx}`}>
                <td className="px-4 py-3"><Skel className="h-3 w-20" /></td>
                <td className="px-4 py-3"><Skel className="h-3 w-16" /></td>
                <td className="px-4 py-3"><Skel className="h-3 w-32" /></td>
                <td className="px-4 py-3 text-right"><Skel className="h-3 w-24" /></td>
              </tr>
            ))}
            {!loading && sortedActions.length === 0 && (
              <tr>
                <td colSpan="4" className="px-4 py-3 text-gray-400 italic text-center">
                  {t?.no_actions || 'No actions yet.'}
                </td>
              </tr>
            )}
            {!loading && sortedActions.map((a, idx) => {
              const isError = String(a.type || '').toUpperCase() === 'ERROR' || String(a.key || '').toLowerCase().includes('error');
              const actionMsg = formatActionMessage(a, t);
              return (
                <tr key={idx} className={`hover:bg-gray-50/80 transition-colors ${isError ? 'bg-red-50/30' : ''}`}>
                  <td className="px-4 py-3 text-gray-700 font-medium whitespace-nowrap">
                    {channelIcon(a.channel)}
                    {a.channel || '-'}
                  </td>
                  <td className="px-4 py-3 text-gray-900">
                    <span className={`inline-block text-[10px] font-bold px-2 py-0.5 rounded border ${
                      isError
                        ? 'bg-red-600 text-white border-red-700 shadow-xs'
                        : 'bg-gray-100 text-gray-700 border-gray-200'
                    }`}>
                      {a.type || '-'}
                    </span>
                    {a.key && (
                      <div className={`text-[10px] font-normal mt-1 ${isError ? 'text-red-500 font-medium' : 'text-gray-400'}`}>
                        {a.key}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-[12px]">
                    {isError ? (
                      <span className="text-red-700 font-medium leading-relaxed">
                        {actionMsg || (t?.error || 'Error')}
                      </span>
                    ) : actionMsg ? (
                      <span className={a.channel === 'EMAIL' ? 'text-amber-800 font-medium' : 'text-gray-700'}>
                        {actionMsg}
                      </span>
                    ) : (
                      <span className="text-gray-300">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right text-gray-700 font-bold tabular-nums whitespace-nowrap">
                    {a.happened_at ? new Date(a.happened_at).toLocaleString(language === 'nl' ? 'nl-NL' : 'en-US') : '-'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ----- Status pill (uses the same className pattern as AdminAfasTransactions.js line 416) -----
const statusPillClass = (status) => {
  const s = String(status || '').toUpperCase();
  if (['SENT', 'DELIVERED', 'RECEIVED'].includes(s)) return 'text-green-600 bg-green-50 border-green-100';
  if (['PENDING', 'SENDING'].includes(s)) return 'text-blue-600 bg-blue-50 border-blue-100';
  // INCOMPLETE = customer data missing (e.g. no VAT); admin fixes + resends.
  if (s === 'INCOMPLETE') return 'text-amber-700 bg-amber-50 border-amber-100';
  // SKIPPED = send not attempted (e.g. Maventa not configured on this env).
  if (s === 'SKIPPED') return 'text-amber-700 bg-amber-50 border-amber-100';
  if (['FAILED', 'REJECTED', 'ERROR'].includes(s)) return 'text-red-600 bg-red-50 border-red-100';
  return 'text-gray-500 bg-gray-50 border-gray-100';
};

// ----- In-flight vs settled -------------------------------------------------
// Mirrors TERMINAL_PEPPOL_STATUSES in backend/routes/peppol.py. A terminal
// allow-list, not an in-flight one: an unrecognised status is then treated as
// still moving (harmless extra poll) rather than as final (wrong).
const TERMINAL_STATUSES = ['DELIVERED', 'REJECTED', 'CANCELLED', 'CANCELED'];
const isSettledStatus = (status) => TERMINAL_STATUSES.includes(String(status || '').toUpperCase());

// How often to re-poll while at least one invoice is still in flight. Maventa
// relay takes minutes, not seconds, and the sweep stops entirely once every row
// has settled — so a slow cadence costs nothing and catches the transition.
const SYNC_INTERVAL_MS = 60000;

// ----- Short pill label: friendly text for INCOMPLETE, raw status otherwise -----
const peppolStatusLabel = (peppol, status, t, hasPdf = false) => {
  if (!peppol) {
    // No send recorded. Distinguish "invoiced but Peppol never attempted"
    // (anomaly) from "invoice not generated yet" (expected) via the PDF marker.
    return hasPdf
      ? (t?.peppol_not_attempted_label || 'Peppol not attempted')
      : (t?.peppol_not_generated_label || 'Not generated yet');
  }
  if (peppol.reason === 'missing_customer_vat') return t?.peppol_incomplete_label || 'Missing VAT';
  if (peppol.reason === 'maventa_not_configured') return t?.peppol_not_configured_label || 'Not configured';
  if (peppol.reason === 'pdf_unavailable') return t?.peppol_pdf_unavailable_label || 'PDF unavailable';
  if (peppol.reason === 'auto_invoice_disabled') return t?.auto_invoice_off || 'Auto Invoice OFF';
  return status;
};

// ----- INCOMPLETE reason -> actionable admin message for the expand panel -----
const peppolReasonMessage = (reason, t) => {
  switch (String(reason || '')) {
    case 'auto_invoice_disabled':
      return t?.peppol_reason_auto_invoice_disabled
        || 'Auto Invoice is disabled for this customer, so the Peppol send was skipped.';
    case 'missing_customer_vat':
      return t?.peppol_reason_missing_customer_vat
        || 'The customer has no VAT number, so no Peppol address could be built. Add the VAT number to the customer record, then click Resend.';
    case 'maventa_not_configured':
      return t?.peppol_reason_maventa_not_configured
        || 'Maventa is not configured on this environment, so the Peppol send was skipped. Set the MAVENTA_* environment variables on the backend, then click Resend.';
    case 'pdf_unavailable':
      return t?.peppol_reason_pdf_unavailable
        || 'The invoice PDF could not be generated, so the Peppol send was skipped. Regenerate the invoice, then click Resend.';
    case 'maventa_rejected':
      return t?.peppol_reason_maventa_rejected
        || 'Maventa rejected the invoice (see the error detail below). Fix the reported issue, then click Resend.';
    case 'auth_error':
      return t?.peppol_reason_auth_error
        || 'Could not authenticate with Maventa (token request failed). Check the Maventa credentials, then click Resend.';
    case 'network_error':
      return t?.peppol_reason_network_error
        || 'Could not reach Maventa (network error). Try Resend again; if it persists, check connectivity.';
    case 'xml_build_error':
      return t?.peppol_reason_xml_build_error
        || 'The invoice could not be converted to Peppol UBL (see the error detail below). This usually means missing order/customer data.';
    case 'generation_error':
      return t?.peppol_reason_generation_error
        || 'Invoice generation failed before the Peppol send (see the error detail below). Resolve the error, then click Resend.';
    default:
      return reason || '';
  }
};

// ----- Invoice PDF link (styled like PdfDownloadButton in AdminAfasTransactions.js).
// Opens the order's Google Drive invoice PDF directly. We do NOT reuse the AFAS
// download here: this screen is Belgium-only and the AFAS dossier (KnSubject)
// upload is rejected for the Belgian administration, so AFAS has no PDF to serve. -----
const InvoicePdfButton = ({ href, t }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    title={t?.view_invoice || 'View invoice PDF'}
    className="flex items-center gap-1.5 px-3 py-1.5 bg-red-50 text-red-600 hover:bg-red-100 border border-red-200 rounded-md transition-colors text-xs font-bold uppercase tracking-wider shadow-sm group"
  >
    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 group-hover:scale-110 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
    </svg>
    {t?.pdf || 'PDF'}
  </a>
);

// ----- Refresh / Resend buttons (styled like PdfDownloadButton in AdminAfasTransactions.js) -----
const RefreshButton = ({ disabled, onClick, busy, t }) => (
  <button
    onClick={onClick}
    disabled={disabled || busy}
    title={disabled ? (t?.peppol_refresh_disabled_hint || 'No Maventa id yet — Resend first') : (t?.peppol_refresh || 'Refresh')}
    className={`flex items-center gap-1.5 px-3 py-1.5 border rounded-md transition-colors text-xs font-bold uppercase tracking-wider shadow-sm group ${
      disabled ? 'bg-gray-50 text-gray-300 border-gray-100 cursor-not-allowed' : 'bg-blue-50 text-blue-600 hover:bg-blue-100 border-blue-200'
    }`}
  >
    <svg xmlns="http://www.w3.org/2000/svg" className={`h-4 w-4 ${busy ? 'animate-spin' : 'group-hover:scale-110 transition-transform'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
    </svg>
    {t?.peppol_refresh || 'Refresh'}
  </button>
);

const ResendButton = ({ onClick, busy, t }) => (
  <button
    onClick={onClick}
    disabled={busy}
    title={t?.peppol_resend || 'Resend'}
    className="flex items-center gap-1.5 px-3 py-1.5 bg-purple-50 text-primary hover:bg-purple-100 border border-purple-200 rounded-md transition-colors text-xs font-bold uppercase tracking-wider shadow-sm group disabled:opacity-50"
  >
    <svg xmlns="http://www.w3.org/2000/svg" className={`h-4 w-4 ${busy ? 'animate-spin' : 'group-hover:scale-110 transition-transform'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
    </svg>
    {t?.peppol_resend || 'Resend'}
  </button>
);

const CreateCreditNoteButton = ({ onClick, busy, t }) => (
  <button
    onClick={onClick}
    disabled={busy}
    title={t?.create_credit_notes || 'Create credit note'}
    className="flex items-center gap-1.5 px-3 py-1.5 bg-red-50 text-red-600 hover:bg-red-100 border border-red-200 rounded-md transition-colors text-xs font-bold uppercase tracking-wider shadow-sm group disabled:opacity-50"
  >
    {busy ? (
      <svg className="animate-spin h-4 w-4 text-current" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
      </svg>
    ) : (
      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 group-hover:scale-110 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    )}
    {t?.create_credit_notes || 'Create credit note'}
  </button>
);

const AdminPeppolInvoices = ({ t, language, customerId }) => {
  // Refresh, Resend and the background sweep all POST, so a read-only account
  // gets none of them — including the sweep, which would otherwise 403 on a
  // timer for as long as the page is open.
  const { can } = usePermissions();
  const canWrite = can(CAP.ADMIN_WRITE);
  // Lightweight list rows from GET /peppol/invoices
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedRows, setExpandedRows] = useState({});
  // Lazy-loaded detail per order: GET /peppol/invoices/<order_id>
  const [details, setDetails] = useState({});
  const [loadingDetails, setLoadingDetails] = useState({});
  const [busy, setBusy] = useState({}); // { [order_id]: 'refresh' | 'resend' }
  // order_id awaiting resend confirmation (drives the ConfirmModal); null = closed
  const [resendConfirmId, setResendConfirmId] = useState(null);
  // Background reconciliation of in-flight Maventa statuses (see syncPending).
  const [syncing, setSyncing] = useState(false);
  const syncingRef = useRef(false);   // guards against overlapping sweeps
  // Mirror of expandedRows for the sweep callback: reading it through a ref keeps
  // syncPending's identity stable, so opening a row can't re-arm the interval.
  const expandedRef = useRef({});
  useEffect(() => { expandedRef.current = expandedRows; }, [expandedRows]);

  const [searchQuery, setSearchQuery] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const apiUrl = process.env.REACT_APP_API_URL || '';

  const fetchRows = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch(`${apiUrl}/peppol/invoices`, { headers: authHeaders() });
      if (!res.ok) throw new Error('Failed to fetch Peppol invoices');
      const data = await res.json();
      let fetchedRows = Array.isArray(data) ? data : [];
      if (customerId) {
        fetchedRows = fetchedRows.filter(r => r.customer_id === customerId);
      }
      setRows(fetchedRows);
    } catch (err) {
      console.error(err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [apiUrl, customerId]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  // useCallback on the two helpers the sweep below depends on, so its own
  // useCallback identity stays stable and the sweep effect doesn't re-arm on
  // every render.
  const fetchDetail = useCallback(async (orderId) => {
    setLoadingDetails(prev => ({ ...prev, [orderId]: true }));
    try {
      const res = await fetch(`${apiUrl}/peppol/invoices/${orderId}`, { headers: authHeaders() });
      if (!res.ok) throw new Error('Failed to fetch detail');
      const data = await res.json();
      setDetails(prev => ({ ...prev, [orderId]: data }));
    } catch (err) {
      console.error(`detail load failed for ${orderId}:`, err);
    } finally {
      setLoadingDetails(prev => ({ ...prev, [orderId]: false }));
    }
  }, [apiUrl]);

  const toggleRow = (orderId) => {
    const willExpand = !expandedRows[orderId];
    setExpandedRows(prev => ({ ...prev, [orderId]: willExpand }));
    // Lazy-load detail the first time the row is opened.
    if (willExpand && !details[orderId] && !loadingDetails[orderId]) {
      fetchDetail(orderId);
    }
  };

  const updateRowPeppolInvoice = useCallback((orderId, peppolInvoice) => {
    // Update both the lightweight list row (for table-level status badge)
    // and the cached detail (for the expanded panel). `reason` is carried too —
    // without it a refresh/resend would downgrade a friendly "Missing VAT" pill
    // back to the raw INCOMPLETE status.
    setRows(prev => prev.map(r => r.order_id === orderId
      ? { ...r, peppol_invoice: peppolInvoice
          ? {
              id: peppolInvoice.id,
              status: peppolInvoice.status,
              destination: peppolInvoice.destination,
              reason: peppolInvoice.reason,
              recipient_email: peppolInvoice.recipient_email || peppolInvoice.recipient?.email,
            }
          : null }
      : r));
    setDetails(prev => prev[orderId]
      ? { ...prev, [orderId]: { ...prev[orderId], peppol_invoice: peppolInvoice } }
      : prev);
  }, []);

  // ----- In-flight reconciliation -------------------------------------------
  // Maventa returns PENDING the instant an invoice is created and can take ~10
  // minutes to actually relay it, with no callback to tell us when it lands. So
  // a stored status is only as fresh as the last poll: without this the table
  // shows PENDING until an admin clicks Refresh on the row. One POST sweeps every
  // in-flight invoice server-side (one OAuth token for the lot) and reports only
  // the rows whose status actually moved.
  const syncPending = useCallback(async () => {
    if (!canWrite) return;                     // read-only: the POST would 403
    if (syncingRef.current) return;            // never stack sweeps
    syncingRef.current = true;
    setSyncing(true);
    try {
      const res = await fetch(`${apiUrl}/peppol/invoices/refresh-pending`, { method: 'POST', headers: authHeaders() });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'sync failed');
      (data.changes || []).forEach(({ order_id, peppol_invoice }) => {
        updateRowPeppolInvoice(order_id, peppol_invoice);
        // An open panel holds its own copy of actions[] and the attempt history,
        // both of which just changed server-side — reload it so the timeline and
        // the history table agree with the new pill.
        if (expandedRef.current[order_id]) fetchDetail(order_id);
      });
      // Backfilled rows kept their pill (they were already settled) but their
      // attempt history was just corrected server-side, so an open panel still
      // needs reloading.
      (data.backfilled || []).forEach((orderId) => {
        if (expandedRef.current[orderId]) fetchDetail(orderId);
      });
    } catch (err) {
      // Deliberately silent: a failed background poll is not admin-actionable,
      // and the per-row Refresh button still reports errors via toast.
      console.error('peppol pending sync failed:', err);
    } finally {
      syncingRef.current = false;
      setSyncing(false);
    }
  }, [apiUrl, canWrite, fetchDetail, updateRowPeppolInvoice]);

  // Boolean, not a count — so re-running the sweep can't re-arm the effect just
  // because a status moved PENDING -> SENT while still in flight.
  const hasInFlight = useMemo(
    () => rows.some(r => r.peppol_invoice?.id && !isSettledStatus(r.peppol_invoice.status)),
    [rows],
  );

  // Sweep once as soon as the list has landed. Unconditional on purpose: the
  // sweep also self-heals attempt histories still frozen on the status captured
  // at send time, which costs no Maventa call and so must happen even when every
  // invoice is already settled.
  useEffect(() => {
    if (loading || error) return;
    syncPending();
  }, [loading, error, syncPending]);

  // Then keep re-polling for as long as anything is in flight, since Maventa
  // will not tell us when it lands. Stops by itself once every row has settled.
  useEffect(() => {
    if (loading || error || !hasInFlight) return undefined;
    const timer = setInterval(syncPending, SYNC_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [loading, error, hasInFlight, syncPending]);

  const handleRefresh = async (orderId) => {
    setBusy(prev => ({ ...prev, [orderId]: 'refresh' }));
    try {
      const res = await fetch(`${apiUrl}/peppol/invoices/${orderId}/refresh`, { method: 'POST', headers: authHeaders() });
      const data = await res.json();
      if (!res.ok) throw Object.assign(new Error('Refresh failed'), { data });
      updateRowPeppolInvoice(orderId, data);
    } catch (err) {
      toast.error(`${t?.peppol_refresh_failed || 'Refresh failed'}: ${errorText(err, t, err.message)}`);
    } finally {
      setBusy(prev => ({ ...prev, [orderId]: null }));
    }
  };

  // Performs the resend. Confirmation is handled by the ConfirmModal (see
  // resendConfirmId), so this no longer shows a native window.confirm.
  const handleResend = async (orderId) => {
    setBusy(prev => ({ ...prev, [orderId]: 'resend' }));
    try {
      const res = await fetch(`${apiUrl}/peppol/invoices/${orderId}/resend`, { method: 'POST', headers: authHeaders() });
      const data = await res.json();
      if (!res.ok) throw Object.assign(new Error(data.status || 'Resend failed'), { data });
      updateRowPeppolInvoice(orderId, data);
      // Reload detail so the new attempt appears in the history table.
      fetchDetail(orderId);
      if (data.status === 'INCOMPLETE') {
        // Resend ran but the customer data is still incomplete (e.g. no VAT).
        toast.error(peppolReasonMessage(data.reason, t));
      } else {
        toast.success(t?.peppol_resend_success || 'Peppol invoice resent');
      }
    } catch (err) {
      toast.error(`${t?.peppol_resend_failed || 'Resend failed'}: ${errorText(err, t, err.message)}`);
    } finally {
      setBusy(prev => ({ ...prev, [orderId]: null }));
    }
  };

  const handleCreateCreditNote = async (orderId) => {
    if (!window.confirm(t?.confirm_create_credit_note || `Are you sure you want to create a credit note for order ${orderId}?`)) {
      return;
    }
    setBusy(prev => ({ ...prev, [orderId]: 'credit_note' }));
    const loadingToast = toast.loading(t?.creating_credit_note || 'Creating credit note...');
    try {
      const res = await fetch(`${apiUrl}/api/orders/${orderId}/credit-note`, {
        method: 'POST',
        headers: authHeaders(),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(errorText(data, t, 'Failed to create credit note'));
      }
      toast.success(data.message || `Successfully created credit note ${data.new_order_id || ''}!`, { id: loadingToast });
      await fetchRows();
      if (data.new_order_id) {
        toggleRow(data.new_order_id);
      }
    } catch (err) {
      console.error(err);
      toast.error(errorText(err, t, err.message), { id: loadingToast });
    } finally {
      setBusy(prev => ({ ...prev, [orderId]: null }));
    }
  };

  const formatMoney = (n) => `€ ${parseFloat(n || 0).toFixed(2)}`;
  const formatDate = (s) => s ? new Date(s).toLocaleString(language === 'nl' ? 'nl-NL' : 'en-US') : '';

  const filteredRows = useMemo(() => {
    return rows.filter(row => {
      const searchStr = searchQuery.toLowerCase();
      const matchesSearch = !searchStr || 
        (row.order_id && row.order_id.toLowerCase().includes(searchStr)) ||
        (row.customer_name && row.customer_name.toLowerCase().includes(searchStr)) ||
        (row.customer_id && row.customer_id.toLowerCase().includes(searchStr)) ||
        (row.peppol_invoice?.destination && row.peppol_invoice.destination.toLowerCase().includes(searchStr));

      let matchesStart = true;
      let matchesEnd = true;
      if (startDate && row.created_at) {
        matchesStart = new Date(row.created_at) >= new Date(startDate);
      }
      if (endDate && row.created_at) {
        const end = new Date(endDate);
        end.setHours(23, 59, 59, 999);
        matchesEnd = new Date(row.created_at) <= end;
      }
      return matchesSearch && matchesStart && matchesEnd;
    });
  }, [rows, searchQuery, startDate, endDate]);

  return (
    <div className="p-4 sm:p-8 space-y-8">
      <div className="flex flex-col xl:flex-row justify-between items-start xl:items-center mb-6 gap-4">
        <div>
          <h2 className="text-3xl font-bold text-gray-800 flex items-center shrink-0">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 mr-3 bg-primary text-white rounded-full p-1.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
            </svg>
            {t?.admin_peppol_invoices || 'Peppol Invoices'}
          </h2>
          <p className="text-gray-600 mt-2">
            {t?.admin_peppol_invoices_description || 'Status of Peppol e-invoices sent via Maventa.'}
          </p>
          {/* Maventa can take ~10 minutes to relay a freshly created invoice and
              never notifies us, so in-flight rows are re-polled server-side until
              they settle. Surfaced so a status changing on its own isn't a mystery. */}
          {syncing && (
            <p className="text-xs text-blue-600 mt-2 flex items-center gap-1.5">
              <svg className="h-3 w-3 animate-spin" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              {t?.peppol_syncing_pending || 'Checking in-flight invoices with Maventa…'}
            </p>
          )}
        </div>

        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 w-full xl:w-auto mt-4 xl:mt-0">
          <div className="flex items-center gap-2 border border-gray-300 rounded-lg px-3 bg-white w-full sm:w-auto overflow-hidden">
            <svg className="h-5 w-5 text-gray-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full sm:w-32 py-2 focus:outline-none text-sm text-gray-600 bg-transparent"
              title="Start Date"
            />
            <span className="text-gray-300">-</span>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-full sm:w-32 py-2 focus:outline-none text-sm text-gray-600 bg-transparent"
              title="End Date"
            />
          </div>

          <div className="relative w-full sm:w-64 xl:w-80 shrink-0">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
              </svg>
            </div>
            <input
              type="text"
              className="block w-full pl-10 pr-8 py-2 border border-gray-300 rounded-lg leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-primary focus:border-primary sm:text-sm transition-all"
              placeholder={t?.search || 'Search by order nr, customer, destination...'}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-500"
              >
                <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </button>
            )}
          </div>
        </div>
      </div>

      {loading ? (
        <div className="bg-white rounded-lg shadow-sm overflow-hidden border border-gray-200">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-primary">
                <tr>
                  <th scope="col" className="px-6 py-3 border-b border-primary"></th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider border-b border-primary">{t?.order_nr || 'Order Nr'}</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider border-b border-primary">{t?.customer || 'Customer'}</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider border-b border-primary">{t?.total_price || 'Total'}</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider border-b border-primary">{t?.created_at || 'Created'}</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider border-b border-primary">{t?.peppol_status || 'Peppol Status'}</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider border-b border-primary">{t?.peppol_destination || 'Destination'}</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {Array.from({ length: 6 }).map((_, idx) => (
                  <tr key={`skel-${idx}`} className="border-b">
                    <td className="px-6 py-4 w-10"><Skel className="h-4 w-4 rounded" /></td>
                    <td className="px-6 py-4"><Skel className="h-4 w-24" /></td>
                    <td className="px-6 py-4"><Skel className="h-4 w-36" /></td>
                    <td className="px-6 py-4"><Skel className="h-4 w-16" /></td>
                    <td className="px-6 py-4"><Skel className="h-4 w-28" /></td>
                    <td className="px-6 py-4"><Skel className="h-5 w-20 rounded-md" /></td>
                    <td className="px-6 py-4"><Skel className="h-4 w-16" /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : error ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center text-red-600">
          {(t?.error_title || 'Error')}: {error}
        </div>
      ) : filteredRows.length === 0 ? (
        <div className="text-gray-500">{t?.no_data_found || 'No data found.'}</div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm overflow-hidden border border-gray-200">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-primary">
                <tr>
                  <th scope="col" className="px-6 py-3 border-b border-primary"></th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider border-b border-primary">{t?.order_nr || 'Order Nr'}</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider border-b border-primary">{t?.customer || 'Customer'}</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider border-b border-primary">{t?.total_price || 'Total'}</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider border-b border-primary">{t?.created_at || 'Created'}</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider border-b border-primary">{t?.peppol_status || 'Peppol Status'}</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider border-b border-primary">{t?.peppol_destination || 'Destination'}</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredRows.map((row, index) => {
                  const isExpanded = !!expandedRows[row.order_id];
                  // Heavy detail (full peppol_invoice with actions, recipient,
                  // customer VAT etc.) is lazy-loaded on expand.
                  const detail = details[row.order_id];
                  const detailLoading = !!loadingDetails[row.order_id];
                  const awaitingDetail = detailLoading && !detail; // true until lazy detail arrives
                  // Use detail's peppol_invoice when available; fall back to
                  // the lightweight list row for the table-level badge.
                  const peppol = detail?.peppol_invoice ?? row.peppol_invoice;
                  const status = peppol?.status || 'NOT_SENT';
                  // invoicePdfId marks that invoice generation ran; lets us tell
                  // "generated but Peppol not attempted" from "not generated yet".
                  const hasPdf = !!(detail?.invoice_pdf_id ?? row.invoice_pdf_id);
                  // Google Drive link to the generated invoice PDF (arrives with detail on expand).
                  const pdfLink = detail?.invoice_pdf_link || null;
                  const statusLabel = peppolStatusLabel(peppol, status, t, hasPdf);
                  const isNotAttemptedAnomaly = !peppol && hasPdf;
                  const statusClass = isNotAttemptedAnomaly
                    ? 'text-amber-700 bg-amber-50 border-amber-100'
                    : statusPillClass(status);
                  const destination = peppol?.destination || '';
                  const hasMaventaId = !!peppol?.id;

                  return (
                    <React.Fragment key={row.order_id || index}>
                      <tr className="border-b hover:bg-gray-50 cursor-pointer" onClick={() => toggleRow(row.order_id)}>
                        <td className="px-6 py-4 text-gray-500 w-10">
                          {isExpanded ? (
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z" clipRule="evenodd" />
                            </svg>
                          ) : (
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                            </svg>
                          )}
                        </td>
                        <td className="px-6 py-4 font-semibold text-primary">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <span>{row.order_id}</span>
                            {(row.is_credit_note || detail?.is_credit_note) && (
                              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider border bg-red-50 text-red-700 border-red-200">
                                CREDIT NOTE
                              </span>
                            )}
                            {(row.credited_by || detail?.credited_by) && (
                              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider border bg-amber-50 text-amber-700 border-amber-200" title={`Credited by ${row.credited_by || detail?.credited_by}`}>
                                CREDITED
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-800">{row.customer_name || row.customer_id || '-'}</td>
                        <td className="px-6 py-4 text-sm text-gray-800 font-medium">{formatMoney(row.total_price)}</td>
                        <td className="px-6 py-4 text-sm text-gray-600">{formatDate(row.created_at)}</td>
                        <td className="px-6 py-4">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-bold border ${statusClass}`}>
                            {statusLabel}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-700">
                          {destination ? (
                            <span className="inline-block bg-gray-100 text-gray-700 text-[10px] font-bold px-2 py-0.5 rounded border border-gray-200">{destination}</span>
                          ) : <span className="text-gray-300">—</span>}
                        </td>
                      </tr>

                      {isExpanded && (
                        <tr className="bg-gray-50">
                          <td colSpan="7" className="px-6 py-6 border-b bg-gray-50/50">
                            {/* No standalone spinner — the skeletons below reserve the
                                final layout height so the panel never jumps on load. */}
                            {/* Actionable banner: explains why the invoice was not sent and what to do */}
                            {peppol?.reason && (
                              <div className="mb-6 flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" viewBox="0 0 20 20" fill="currentColor">
                                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                </svg>
                                <div>
                                  <p className="text-sm font-semibold text-amber-800">{t?.peppol_action_needed || 'Action needed'}</p>
                                  <p className="text-sm text-amber-700 mt-0.5">{peppolReasonMessage(peppol.reason, t)}</p>
                                </div>
                              </div>
                            )}
                            {/* Anomaly: invoice was generated (has PDF) but no Peppol
                                send was ever recorded. No reason to show — prompt Resend. */}
                            {isNotAttemptedAnomaly && (
                              <div className="mb-6 flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" viewBox="0 0 20 20" fill="currentColor">
                                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                </svg>
                                <div>
                                  <p className="text-sm font-semibold text-amber-800">{t?.peppol_action_needed || 'Action needed'}</p>
                                  <p className="text-sm text-amber-700 mt-0.5">
                                    {t?.peppol_not_attempted_message
                                      || 'This invoice was generated but no Peppol send was ever recorded. Click Resend to send it now.'}
                                  </p>
                                </div>
                              </div>
                            )}
                            {/* Order + Peppol info grid */}
                            <div className="mb-8">
                              <div className="flex justify-between items-center mb-4 flex-wrap gap-2">
                                <h4 className="font-semibold text-gray-700 uppercase text-xs tracking-wider flex items-center gap-2">
                                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                  </svg>
                                  <span>{t?.order_information || 'Order Information'}</span>
                                  {(detail?.is_credit_note || row.is_credit_note) && (
                                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider border bg-red-50 text-red-700 border-red-200">
                                      CREDIT NOTE {detail?.credited_invoice ? `(${detail.credited_invoice})` : ''}
                                    </span>
                                  )}
                                  {(detail?.credited_by || row.credited_by) && (
                                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider border bg-amber-50 text-amber-700 border-amber-200">
                                      CREDITED BY {detail?.credited_by || row.credited_by}
                                    </span>
                                  )}
                                </h4>
                                <div className="ml-auto flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                                  {/* View the generated invoice PDF so the admin can see
                                      what the invoice is about. Opens the order's Drive
                                      link; shown once detail loads and a PDF exists. */}
                                  {pdfLink && (
                                    <InvoicePdfButton href={pdfLink} t={t} />
                                  )}
                                  {canWrite && (
                                    <>
                                      {!detail?.is_credit_note && !row.is_credit_note && !detail?.credited_by && !row.credited_by && (
                                        <CreateCreditNoteButton
                                          busy={busy[row.order_id] === 'credit_note'}
                                          onClick={() => handleCreateCreditNote(row.order_id)}
                                          t={t}
                                        />
                                      )}
                                      <RefreshButton
                                        disabled={!hasMaventaId}
                                        busy={busy[row.order_id] === 'refresh'}
                                        onClick={() => handleRefresh(row.order_id)}
                                        t={t}
                                      />
                                      <ResendButton
                                        busy={busy[row.order_id] === 'resend'}
                                        onClick={() => setResendConfirmId(row.order_id)}
                                        t={t}
                                      />
                                    </>
                                  )}
                                </div>
                              </div>
                              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                                {[
                                  { label: t?.peppol_status || 'Peppol Status', value: statusLabel, color: statusClass },
                                  { label: t?.peppol_destination || 'Destination', value: destination || (t?.not_applicable || 'N/A') },
                                  { 
                                    label: t?.peppol_recipient || 'Recipient', 
                                    value: (
                                      <div>
                                        <div>{peppol?.recipient?.name || detail?.customer_name || row.customer_name}</div>
                                        {(peppol?.recipient?.email || peppol?.recipient_email) && (
                                          <div className="text-[10px] text-gray-500 font-normal truncate max-w-[200px]" title={peppol?.recipient?.email || peppol?.recipient_email}>
                                            {peppol?.recipient?.email || peppol?.recipient_email}
                                          </div>
                                        )}
                                      </div>
                                    ) 
                                  },
                                  { label: t?.environment || 'Environment', value: peppol?.environment || '-', loading: awaitingDetail },
                                  { label: t?.customer_id || 'Customer ID', value: (
                                      <div className="flex items-center gap-2">
                                        <Link 
                                          to={`/admin/customers/${detail?.customer_id || row.customer_id}`} 
                                          className="text-primary hover:underline font-bold" 
                                          onClick={(e) => e.stopPropagation()}
                                        >
                                          {detail?.customer_id || row.customer_id}
                                        </Link>
                                        {(detail?.customer_domain || row.customer_domain) && (
                                          <span className="bg-gray-100 text-gray-500 border border-gray-200 text-[10px] px-1.5 py-0.5 rounded-sm truncate max-w-[150px]">
                                            {detail?.customer_domain || row.customer_domain}
                                          </span>
                                        )}
                                      </div>
                                    ) 
                                  },
                                  { label: t?.order_user || 'Order User', value: detail?.order_user || '-', loading: awaitingDetail },
                                  { label: t?.subtotal || 'Subtotal', value: formatMoney(detail?.subtotal ?? 0), loading: awaitingDetail },
                                  { label: t?.total_price || 'Total Price', value: formatMoney(detail?.total_price ?? row.total_price), highlight: true },
                                ].map((stat, idx) => (
                                  <div key={idx} className={`p-3 rounded-lg border bg-white shadow-sm flex flex-col justify-between ${stat.highlight ? 'border-primary/20 ring-1 ring-primary/5' : 'border-gray-200'}`}>
                                    <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">{stat.label}</span>
                                    <span className={`text-sm font-semibold truncate flex items-center gap-2 ${stat.color ? `px-2 py-0.5 rounded-md inline-flex w-fit border ${stat.color}` : (stat.highlight ? 'text-primary' : 'text-gray-800')}`}>
                                      {stat.loading ? <Skel className="h-4 w-20" /> : (stat.value || (t?.not_applicable || 'N/A'))}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>

                            {/* Maventa raw fields */}
                            {peppol && (
                              <div className="mb-6 last:mb-0">
                                <h4 className="font-semibold text-gray-700 mb-2 uppercase text-[10px] tracking-widest flex items-center">
                                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                                  </svg>
                                  {t?.peppol_details || 'Peppol details'}
                                </h4>
                                <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white shadow-sm">
                                  <table className="w-full text-sm text-left">
                                    <tbody className="divide-y divide-gray-100">
                                      {awaitingDetail
                                        ? Array.from({ length: 5 }).map((_, idx) => (
                                            <tr key={`skel-${idx}`}>
                                              <td className="px-4 py-2 w-1/3 align-top"><Skel className="h-3 w-24" /></td>
                                              <td className="px-4 py-2"><Skel className="h-3 w-40" /></td>
                                            </tr>
                                          ))
                                        : [
                                            ['Maventa ID', peppol.id],
                                            [t?.peppol_recipient || 'Recipient', peppol.recipient?.name],
                                            [t?.peppol_recipient_email || 'Recipient email', peppol.recipient?.email || peppol.recipient_email],
                                            ['Recipient endpoint', peppol.recipient?.endpoint],
                                            ['Recipient country', peppol.recipient?.country],
                                            ['Sent at', formatDate(peppol.sent_at)],
                                            ['Last attempted at', formatDate(peppol.attemptedAt)],
                                            ['Last refreshed at', formatDate(peppol.refreshedAt)],
                                            [t?.error || 'Error', peppol.error],
                                          ].filter(([, v]) => v != null && v !== '').map(([k, v], idx) => (
                                            <tr key={idx} className="hover:bg-gray-50/80 transition-colors">
                                              <td className="px-4 py-2 text-[11px] font-bold text-gray-500 uppercase tracking-wider w-1/3 align-top">{k}</td>
                                              <td className="px-4 py-2 text-gray-800 text-[12px] break-all">{v}</td>
                                            </tr>
                                          ))}
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            )}

                            {peppol && (
                              <MaventaActionsTimeline actions={peppol.actions || []} language={language} t={t} loading={awaitingDetail} />
                            )}

                            {/* Attempt history — every send/resend is appended server-side (peppolAttempts) */}
                            {detail?.peppol_attempts?.length > 0 && (
                              <div className="mt-6">
                                <h4 className="font-semibold text-gray-700 mb-2 uppercase text-[10px] tracking-widest flex items-center">
                                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                  </svg>
                                  {t?.peppol_attempts_history || 'Attempt history'}
                                </h4>
                                <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white shadow-sm">
                                  <table className="w-full text-sm text-left">
                                    <thead className="bg-gray-50 text-gray-500 uppercase text-[10px] font-bold tracking-wider">
                                      <tr>
                                        <th className="px-4 py-2">{t?.happened_at || 'When'}</th>
                                        <th className="px-4 py-2">{t?.peppol_status || 'Peppol Status'}</th>
                                        <th className="px-4 py-2">{t?.peppol_destination || 'Destination'}</th>
                                        <th className="px-4 py-2">{t?.peppol_attempt_detail || 'Detail'}</th>
                                      </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100">
                                      {[...detail.peppol_attempts].reverse().map((att, idx) => (
                                        <tr key={idx} className="hover:bg-gray-50/80 transition-colors align-top">
                                          <td className="px-4 py-2 text-gray-700 text-[12px] whitespace-nowrap">
                                            {formatDate(att.attemptedAt)}
                                            {/* The status above is the attempt's OUTCOME, observed later by
                                                a poll — Maventa is still PENDING when the send returns. */}
                                            {att.statusRefreshedAt && (
                                              <div className="text-[10px] text-gray-400 font-normal mt-0.5">
                                                {(t?.peppol_outcome_seen_at || 'outcome seen {{when}}')
                                                  .replace('{{when}}', formatDate(att.statusRefreshedAt))}
                                              </div>
                                            )}
                                          </td>
                                          <td className="px-4 py-2">
                                            <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-bold border ${statusPillClass(att.status)}`}>
                                              {att.reason === 'missing_customer_vat' ? (t?.peppol_incomplete_label || 'Missing VAT') : att.status}
                                            </span>
                                          </td>
                                          <td className="px-4 py-2 text-gray-600 text-[12px]">{att.destination || '—'}</td>
                                          <td className="px-4 py-2 text-gray-600 text-[12px] break-all">
                                            {att.error || (att.reason ? peppolReasonMessage(att.reason, t) : (
                                              <div>
                                                <div>{att.recipient?.name || '—'}</div>
                                                {(att.recipient?.email || att.recipient_email) && (
                                                  <div className="text-[11px] text-gray-500 font-mono mt-0.5 flex items-center gap-1">
                                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 text-amber-500 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                                    </svg>
                                                    <span>{att.recipient?.email || att.recipient_email}</span>
                                                  </div>
                                                )}
                                              </div>
                                            ))}
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={!!resendConfirmId}
        title={t?.peppol_resend || 'Resend'}
        message={(t?.peppol_resend_confirm || 'Resend Peppol invoice for order {{order_id}}?').replace('{{order_id}}', resendConfirmId || '')}
        confirmLabel={t?.peppol_resend || 'Resend'}
        cancelLabel={t?.cancel || 'Cancel'}
        busy={busy[resendConfirmId] === 'resend'}
        onCancel={() => setResendConfirmId(null)}
        onConfirm={async () => {
          const id = resendConfirmId;
          await handleResend(id);
          setResendConfirmId(null);
        }}
      />
    </div>
  );
};

export default AdminPeppolInvoices;
